import React, { useState } from 'react'
import { Modal, ModalKind } from '@aposphaere/ui-components'
import { useModal } from '../../contexts/modalContext'
import { useGetCrmSurveysQuery, useCreateCrmSurveyAnswersMutation } from '../../hooks/graphql/survey'

type Props = {
  pharmacyId: number
}

const SurveyModal: React.FC<Props> = ({ pharmacyId }) => {
  const { data: surveys, isLoading, isError } = useGetCrmSurveysQuery({ pharmacyId: String(pharmacyId) })
  const { mutate: submitAnswers, isLoading: isSubmitting } = useCreateCrmSurveyAnswersMutation()
  const { closeModal } = useModal()

  const [answers, setAnswers] = useState<Record<string, string>>({})

  const allAnswered = (surveys?.length ?? 0) > 0
    && (surveys?.every((s) => answers[s.id] !== undefined) ?? false)

  const handleSubmit = () => {
    if (!allAnswered) return

    const crm_surveys = Object.entries(answers).map(([crm_survey_id, crm_survey_answer_option_id]) => ({
      crm_survey_id: Number(crm_survey_id),
      crm_survey_answer_option_id: Number(crm_survey_answer_option_id),
    }))

    submitAnswers(
      { pharmacy_id: pharmacyId, crm_surveys },
      { onSuccess: closeModal }
    )
  }

  return (
    <Modal kind={ModalKind.sm} title="Umfrage" onClose={closeModal} onBack={() => null}>
      {isLoading && <div className="p-4 text-center text-gray-500">Lade Daten…</div>}

      {isError && (
        <div className="p-4 text-center text-red-500">
          Fehler beim Laden der Umfrage.
        </div>
      )}

      {!isLoading && !isError && surveys && (
        <div className="flex flex-col gap-6 p-4">
          {surveys.map((survey) => (
            <div key={survey.id} className="flex flex-col gap-2">
              <p className="font-medium text-gray-800">{survey.question}</p>
              <div className="flex flex-col gap-1">
                {survey.crm_survey_answer_options.map((option) => {
                  const isSelected = answers[survey.id] === option.id
                  return (
                    <label
                      key={option.id}
                      className={`flex items-center gap-2 cursor-pointer rounded px-3 py-2 border transition-colors ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50 text-blue-800'
                          : 'border-gray-200 hover:bg-gray-50 text-gray-700'
                      }`}
                    >
                      <input
                        type="radio"
                        name={`survey-${survey.id}`}
                        value={option.id}
                        checked={isSelected}
                        onChange={() => setAnswers((prev) => ({ ...prev, [survey.id]: option.id }))}
                        className="accent-blue-500"
                      />
                      {option.label}
                    </label>
                  )
                })}
              </div>
            </div>
          ))}

          <button
            onClick={handleSubmit}
            disabled={!allAnswered || isSubmitting}
            className={`mt-2 w-full rounded px-4 py-2 font-medium text-white transition-colors ${
              allAnswered && !isSubmitting
                ? 'bg-blue-600 hover:bg-blue-700'
                : 'bg-gray-300 cursor-not-allowed'
            }`}
          >
            {isSubmitting ? 'Wird gespeichert…' : 'Absenden'}
          </button>
        </div>
      )}
    </Modal>
  )
}

export default SurveyModal